import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { UsersAssignmentsByOperatorParams } from 'src/apis/assignment-service/types';
import { useAssignmentInfo } from 'src/apis/assignment-service/use-assignment-info';
import type { AssignmentServiceRequest } from 'src/apis/assignment-service/use-assignment-service-root';
import { useAssignmentServicePublic } from 'src/apis/assignment-service/use-assignment-service-root';
import { useMyAssignmentsOptional } from 'src/apis/assignment-service/use-my-assignments';
import { usePublicRequiredDocuments } from 'src/apis/assignment-service/use-public-required-documents';
import { useUserAssignmentsByOperator } from 'src/apis/assignment-service/use-user-assignments-by-operator';
import { useContractManagement } from 'src/apis/contract-management/use-contract-management';
import { useOperatorByTso } from 'src/apis/contract-management/use-operator-by-tso';
import { useOldUserId } from 'src/apis/monolith/use-get-old-user-id';
import { useOperatorPlatformId } from 'src/apis/monolith/use-operator-platform-id';
import { useOptionalTso } from 'src/apis/monolith/use-tso';
import { useUserAssignment } from 'src/apis/monolith/use-user-assignment';
import type { ContactsParams } from 'src/apis/organisation-service/types';
import { useOptionalContacts } from 'src/apis/organisation-service/use-optional-contacts';
import { useOrganisationService } from 'src/apis/organisation-service/use-organisation-service';
import { useOrganisationServicePublic } from 'src/apis/organisation-service/use-organisation-service-public';
import type { PublicOperatorDetailsRequest } from 'src/apis/organisation-service/use-public-operator-details';
import { usePublicOperatorDetails } from 'src/apis/organisation-service/use-public-operator-details';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { isNetworkError } from 'src/hooks/use-axios';
import { useAssignmentsAndContactsParams } from 'src/pages/operators/assignments/details/use-page-params';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

export const AssigmentDetails = () => {
  const { operatorUuid, userUuid } = useParams<{
    operatorUuid: string;
    userUuid: string;
  }>();

  const tsoPlatformIds = useOperatorPlatformId(operatorUuid);
  const oldUserId = useOldUserId({ userUuid });
  const assignmentService = useAssignmentServicePublic();
  const organisationServicePublic = useOrganisationServicePublic();
  const operatorDetailsResponse = usePublicOperatorDetails({
    organisationServicePublic,
    operatorUuid,
  });

  if (
    oldUserId?.response === null ||
    (!tsoPlatformIds.response && !tsoPlatformIds.error) ||
    !operatorDetailsResponse.response ||
    !assignmentService.response
  )
    return <PageSpinner />;

  if (oldUserId?.response.data.customerUserId === undefined)
    throw new Forbidden();

  return (
    <AssignmentsDetailsWithOldId
      oldTsoId={tsoPlatformIds.response?.data.referenceId.toString()}
      userId={oldUserId.response.data.customerUserId.toString()}
      operatorDetailsResponse={operatorDetailsResponse}
      operatorUuid={operatorUuid}
      assignmentService={assignmentService}
    />
  );
};

type Props = {
  oldTsoId?: string;
  operatorUuid: string;
  userId: string;
  operatorDetailsResponse: Successful<PublicOperatorDetailsRequest>;
  assignmentService: Successful<AssignmentServiceRequest>;
};

const AssignmentsDetailsWithOldId: FC<Props> = ({
  oldTsoId,
  operatorUuid,
  userId,
  operatorDetailsResponse,
  assignmentService,
}) => {
  const tso = useOptionalTso(oldTsoId);
  const contractManagement = useContractManagement();
  const organisationService = useOrganisationService();

  const userAssignment = useUserAssignment({
    tsoId: oldTsoId,
    userId,
  });
  const caMaOperator = useOperatorByTso({ operatorUuid });
  const assignmentInfo = useAssignmentInfo({
    assignmentService,
    operatorId: operatorUuid,
  });

  const pageParams = useAssignmentsAndContactsParams();

  const params = useMemo(
    () =>
      ({
        limit: pageParams.contactDetails.value.pageSize,
        offset: pageParams.contactDetails.value.start,
      }) satisfies ContactsParams,
    [pageParams.contactDetails.value]
  );

  const paramsUserAssignments = useMemo(() => {
    return {
      sortBy: pageParams.userAssignmentsByOperator.value.sortBy,
      sortDirection: pageParams.userAssignmentsByOperator.value.sortDirection,
      limit: pageParams.userAssignmentsByOperator.value.pageSize,
      offset: pageParams.userAssignmentsByOperator.value.start,
      userName: pageParams.userAssignmentsByOperator.value.userName,
      userEmail: pageParams.userAssignmentsByOperator.value.userEmail,
      status: pageParams.userAssignmentsByOperator.value.status,
    } satisfies UsersAssignmentsByOperatorParams;
  }, [pageParams.userAssignmentsByOperator.value]);

  const userAssignments = useUserAssignmentsByOperator({
    url: assignmentService.response.data._links.getUserAssignmentsByOperator
      ?.href,
    params: paramsUserAssignments,
    operatorId: operatorUuid,
  });

  const contacts = useOptionalContacts({
    assignmentInfo,
    params,
  });

  const myAssignmentsRequest = useMyAssignmentsOptional({
    assignmentService,
    operatorId: operatorUuid,
  });

  const publicRequiredDocuments = usePublicRequiredDocuments({
    assignmentService,
    operatorId: operatorUuid,
  });

  if (
    tso?.response === null ||
    !publicRequiredDocuments.response ||
    contacts?.response === null ||
    userAssignment?.response === null ||
    myAssignmentsRequest?.response === null ||
    caMaOperator === null || // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    (!contractManagement.response &&
      !isNetworkError(contractManagement.error)) ||
    !assignmentInfo.response ||
    !organisationService.response ||
    userAssignments?.response === null
  ) {
    return <PageSpinner />;
  }

  return (
    <Page
      tsoId={oldTsoId}
      tso={tso}
      contractManagement={contractManagement}
      userAssignment={userAssignment}
      publicRequiredDocuments={publicRequiredDocuments}
      operatorDetailsResponse={operatorDetailsResponse}
      assignmentInfo={assignmentInfo}
      contacts={contacts}
      assignmentService={assignmentService}
      organisationService={organisationService}
      myAssignmentsRequest={myAssignmentsRequest}
      userAssignments={userAssignments}
      paramsUserAssignments={pageParams}
      operatorUuid={operatorUuid}
    />
  );
};
